import { useMemo, useState } from "react";
import { SectionShell } from "@/pages/SectionShell";
import { Button } from "@/components/Button";
import { ConfirmDialog } from "@/components/ConfirmDialog";
import { EmptyState } from "@/components/EmptyState";
import { useI18n, useT } from "@/contexts/I18nContext";
import { useToast } from "@/contexts/ToastContext";
import { useCallable } from "@/lib/hooks/useCallable";
import { call, CALLABLES } from "@/services/callables";
import { formatDateTime } from "@/lib/format";
import {
  STATE_KEY,
  STATE_TONE,
  lastDryRun,
  readiness,
  runsFor,
  stateOf,
  type Migration,
  type MigrationRun,
} from "./migrations";

/**
 * Migrations.
 *
 * **A dry run is required before a real one, and the server is what enforces
 * it.** This page shows the rule rather than implementing it: the live button
 * is disabled with the reason on screen, because "why can I not press this" is
 * a question a console should answer where it is asked.
 *
 * The confirmation is the number the dry run reported, typed. Somebody who has
 * not read it cannot type it.
 */
export function MigrationsPage() {
  const t = useT();
  const { locale } = useI18n();
  const { success, showError } = useToast();

  const listing = useCallable<{ migrations: Migration[]; runs: MigrationRun[] }>(
    CALLABLES.listMigrations
  );
  const [busy, setBusy] = useState("");
  const [applying, setApplying] = useState<Migration | undefined>(undefined);

  const migrations = useMemo(() => listing.data?.migrations ?? [], [listing.data]);
  const runs = useMemo(() => listing.data?.runs ?? [], [listing.data]);

  async function execute(migrationId: string, mode: "dry" | "live", reason?: string) {
    const result = await call<
      { migrationId: string; mode: "dry" | "live"; reason?: string },
      MigrationRun
    >(CALLABLES.runDataMigration, {
      migrationId,
      mode,
      ...(reason ? { reason } : {}),
    });

    success(mode === "dry" ? "migration.dryRunDone" : "migration.applied", {
      count: result.affected,
    });
    listing.reload();
  }

  return (
    <SectionShell titleKey="migration.title">
      <p className="max-w-3xl text-sm text-ink-soft">{t("migration.intro")}</p>
      <p className="mt-2 max-w-3xl text-sm text-ink-muted">{t("migration.idempotent")}</p>

      {listing.loading ? (
        <p className="mt-8 text-sm text-ink-muted">{t("common.loading")}</p>
      ) : migrations.length === 0 ? (
        <EmptyState titleKey="migration.empty" bodyKey="migration.emptyBody" />
      ) : (
        <div className="mt-8 flex flex-col gap-6">
          {migrations.map((migration) => {
            const state = stateOf(runs, migration.id);
            const verdict = readiness(runs, migration.id);
            const dry = lastDryRun(runs, migration.id);
            const history = runsFor(runs, migration.id);

            return (
              <article
                key={migration.id}
                className="rounded-md border border-paper-deep bg-white px-5 py-4"
              >
                <div className="flex flex-wrap items-baseline justify-between gap-3">
                  <h2 className="font-display text-base text-ink">{migration.description}</h2>
                  <span className={`text-sm ${STATE_TONE[state]}`}>{t(STATE_KEY[state])}</span>
                </div>
                <p className="mt-1 font-mono text-xs text-ink-muted">{migration.id}</p>

                <div className="mt-4 flex flex-wrap items-center gap-3">
                  <Button
                    variant="secondary"
                    busy={busy === `${migration.id}:dry`}
                    onClick={async () => {
                      setBusy(`${migration.id}:dry`);
                      try {
                        await execute(migration.id, "dry");
                      } catch (error) {
                        showError(error);
                      } finally {
                        setBusy("");
                      }
                    }}
                  >
                    {t("migration.dryRun")}
                  </Button>

                  <Button
                    disabled={!verdict.ready}
                    onClick={() => setApplying(migration)}
                  >
                    {t("migration.apply")}
                  </Button>

                  {verdict.ready ? (
                    <span className="text-sm text-ink-muted">
                      {t("migration.wouldChange", { count: verdict.affected })}
                    </span>
                  ) : (
                    /*
                      The reason, beside the disabled button. Hiding it would
                      leave somebody clicking a dead control and guessing.
                    */
                    <span className="text-sm text-ink-muted">{t(verdict.reasonKey)}</span>
                  )}
                </div>

                {history.length > 0 ? (
                  <table className="mt-5 w-full border-collapse text-sm">
                    <caption className="sr-only">
                      {t("migration.history", { id: migration.id })}
                    </caption>
                    <thead>
                      <tr className="border-b border-paper-deep text-left text-ink-muted">
                        <th scope="col" className="px-3 py-2 font-medium">{t("migration.column.when")}</th>
                        <th scope="col" className="px-3 py-2 font-medium">{t("migration.column.mode")}</th>
                        <th scope="col" className="px-3 py-2 font-medium">{t("migration.column.affected")}</th>
                        <th scope="col" className="px-3 py-2 font-medium">{t("migration.column.outcome")}</th>
                      </tr>
                    </thead>
                    <tbody>
                      {history.slice(0, 5).map((run) => (
                        <tr key={run.id} className="border-b border-paper-warm last:border-0">
                          <td className="px-3 py-2 text-ink-muted">
                            {formatDateTime(locale, run.at)}
                          </td>
                          <td className="px-3 py-2 text-ink">
                            {t(run.mode === "dry" ? "migration.mode.dry" : "migration.mode.live")}
                          </td>
                          <td className="px-3 py-2 text-ink">{run.affected}</td>
                          <td className={`px-3 py-2 ${run.ok ? "text-ink-soft" : "text-accent"}`}>
                            {run.ok ? t("migration.ok") : (run.error ?? t("migration.failed"))}
                          </td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                ) : null}

                {applying?.id === migration.id ? (
                  <ConfirmDialog
                    open
                    onClose={() => setApplying(undefined)}
                    onConfirm={async (reason) => {
                      await execute(migration.id, "live", reason);
                      setApplying(undefined);
                    }}
                    titleKey="migration.confirmTitle"
                    titleParams={{ description: migration.description }}
                    bodyKey="migration.confirmBody"
                    expected={String(dry?.affected ?? 0)}
                    confirmKey="migration.confirmType"
                    mismatchKey="migration.mismatch"
                    withReason
                    destructive={false}
                  />
                ) : null}
              </article>
            );
          })}
        </div>
      )}
    </SectionShell>
  );
}
